import { useEffect, useMemo, useState } from "react";
import {
  fetchSalesByPaymentType,
} from "../../../services/dataService";
import { calculateMetrics } from "../utils/metricsCalculations";
import { comparisonFor, comparisonKey } from "../utils/trendPeriod";
import { extractErrorMessage, isReportAccessDenied } from "../../../utils/errors";
import { RESTAURANT_FIELD, buildSelectionOptions } from "../../../utils/restaurant";

/**
 * Хук для загрузки и агрегации метрик + тенденции (дельта выручки к периоду сравнения).
 * periodKey — выбранный период (today, yesterday, week…, 'day' — день из ссылки): от него зависит, с чем
 * сравнивать (utils/trendPeriod). Данные сравнения привязаны к своему периоду — чужие не подставляются.
 */
export const useMetricsData = (
  token,
  dateRange,
  selectedPayType,
  selectedGroup,
  periodKey
) => {
  const [salesData, setSalesData] = useState(null);
  const [previous, setPrevious] = useState(null); // { key, data } — данные сравнения и чей это период
  const [filteredData, setFilteredData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [allPayTypes, setAllPayTypes] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false); // нет прав на отчёты iiko — мягкая карточка

  const comparison = useMemo(() => comparisonFor(periodKey, dateRange), [periodKey, dateRange?.from, dateRange?.to]); // eslint-disable-line react-hooks/exhaustive-deps
  const compareKey = comparisonKey(comparison);

  // Дельта выручки к периоду сравнения (с учётом выбранного ресторана и типа оплаты)
  const calculateTrends = (currentMetrics, previousMetrics) => {
    if (!currentMetrics || !previousMetrics) return null;
    const currentSum = currentMetrics.totalSum || 0;
    const previousSum = previousMetrics.totalSum || 0;
    let delta = 0;
    if (previousSum > 0) delta = ((currentSum - previousSum) / previousSum) * 100;
    else if (currentSum > 0) delta = 100;
    return {
      label: comparison?.label || "",
      current: { sales: currentSum },
      previous: { sales: previousSum },
      delta: Math.round(delta * 100) / 100, // Округляем до 2 знаков после запятой
    };
  };

  // 1️⃣ Загрузка данных с сервера
  useEffect(() => {
    console.count("🔁 useMetricsData fired");

    let cancelled = false;

    const loadData = async () => {
      if (!token || !dateRange?.from || !dateRange?.to) return;

      try {
        setLoading(true);
        setAccessDenied(false);

        // 🔹 Получаем активный сервер
        const cachedServer = localStorage.getItem("activeServer");
        const server = cachedServer ? JSON.parse(cachedServer) : null;
        if (!server) {
          console.warn("⚠️ Сервер не найден в localStorage, пропускаем загрузку");
          return;
        }

        // 🔹 Загружаем метрики с сервера
        const data = await fetchSalesByPaymentType(token, server, dateRange);
        if (cancelled) return;
        setSalesData(data);

        // 🔹 Формируем списки фильтров
        const { payTypes, groups } = extractOptions(data);
        setAllPayTypes(["Все", ...payTypes]);
        setAllGroups(["Все", ...groups]);

        // 🔹 Начальный подсчёт агрегированных метрик
        // fetchSalesByPaymentType уже возвращает объект sales_data
        const metrics = calculateMetrics(
          data,
          selectedPayType,
          selectedGroup
        );
        setFilteredData(metrics);

      } catch (err) {
        console.error("❌ Ошибка загрузки метрик:", err);
        if (!cancelled) {
          if (isReportAccessDenied(err)) setAccessDenied(true);
          else setError(extractErrorMessage(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [token, dateRange?.from, dateRange?.to]);

  // Данные сравнения — отдельным запросом, со своим ключом периода
  useEffect(() => {
    if (!token || !comparison) return undefined;
    let cancelled = false;
    const server = JSON.parse(localStorage.getItem("activeServer") || "null");
    if (!server) return undefined;
    fetchSalesByPaymentType(token, server, comparison, { untilNow: comparison.untilNow })
      .then((data) => {
        if (!cancelled) setPrevious({ key: compareKey, data });
      })
      .catch((err) => {
        console.warn("⚠️ Ошибка при загрузке данных для сравнения:", err);
        if (!cancelled) setPrevious(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, compareKey]);

  // 2️⃣ Пересчёт метрик и трендов при изменении фильтров без повторной загрузки
  useEffect(() => {
    if (!salesData) return;

    // fetchSalesByPaymentType уже возвращает объект sales_data
    const metrics = calculateMetrics(
      salesData,
      selectedPayType,
      selectedGroup
    );
    setFilteredData(metrics);

    // Тенденция — только если данные сравнения от текущего периода (при смене периода чужие не показываем)
    if (previous?.data && previous.key === compareKey) {
      const previousMetrics = calculateMetrics(previous.data, selectedPayType, selectedGroup);
      setTrends(calculateTrends(metrics, previousMetrics));
    } else {
      setTrends(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesData, previous, selectedPayType, selectedGroup, compareKey]);

  return {
    salesData,
    filteredData,
    trends,
    allPayTypes,
    allGroups,
    loading,
    error,
    accessDenied,
  };
};

// 🔹 Вспомогательная функция: извлечение типов оплат и групп
export function extractOptions(salesData) {
  if (!salesData) return { payTypes: [], groups: [] };

  // Строки отчёта: { data: [...] } или старый вид { "Наличные": { details | data: [...] } }
  const rows = [];
  if (Array.isArray(salesData.data)) rows.push(...salesData.data);
  Object.values(salesData).forEach((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return;
    if (Array.isArray(item.details)) rows.push(...item.details);
    else if (Array.isArray(item.data)) rows.push(...item.data);
    else if (item.PayTypes || item[RESTAURANT_FIELD]) rows.push(item);
  });

  const payTypes = new Set(rows.map((row) => row?.PayTypes).filter(Boolean));
  return {
    payTypes: Array.from(payTypes),
    // Рестораны и группы в них (см. utils/restaurant)
    groups: buildSelectionOptions(rows),
  };
}
